import { useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { TextInputSmall } from "../inputs/TextInput";
import { namespaces } from "../../consts/i18n";
import { RecordAreaType } from "../../api/consts";
import { computeArea, isValidPolygon } from "../../utils/geo";
import TogglesSelect from "../inputs/TogglesSelect";
import { getErrorForPath } from "../../utils/serverIssuesUtils";
import useAuthInfo from "../../hooks/useAuthInfo";
import { formatMetersToString } from "../../utils/stringUtils";

function FacilityForm({ data, onChange, issues }) {
  const { t } = useTranslation(namespaces.viewer);

  const { canEdit } = useAuthInfo();

  const setData = useCallback(
    (newData) => {
      onChange(newData);
    },
    [onChange],
  );

  const handleTextChange = (name) => (value) => {
    setData({ ...data, [name]: value });
  };

  const handleSelectChange = (name) => (selectedOption) => {
    const { value } = selectedOption;
    setData({ ...data, [name]: value });
  };

  const areaOptions = Object.values(RecordAreaType).map((area) => ({
    value: area,
    label: t(`form.${area}`),
  }));

  const isAreaSet = useRef(false);

  useEffect(() => {
    if (data?.points && isAreaSet.current === false) {
      isAreaSet.current = true;
      if (isValidPolygon(data.points) === false) {
        return setData({ ...data, size: "invalid" });
      }

      const area = computeArea(data.points);
      setData({ ...data, size: area });
    }
  }, [data?.points, setData, data]);

  const size = useMemo(() => {
    if (data.size) {
      if (data.size === "invalid") {
        return "invalid";
      }

      return formatMetersToString({ length: data.size, units: "m²" });
    }
    return "";
  }, [data.size]);

  return (
    <div className="flex flex-col gap-[20px] bg-bg1 rounded-xl pb-[25px]">
      <div
        className="
          flex flex-row items-start justify-between
          w-full gap-[24px]
        "
      >
        <TogglesSelect
          label={t("form.type")}
          disabled={!canEdit}
          options={areaOptions}
          selectedOption={data.type}
          extraError={getErrorForPath(issues, ["type"])}
          onChange={handleSelectChange("type")}
        />
      </div>
      <div className="flex flex-row gap-[24px]">
        <div className="w-1/2">
          {size === "invalid" ? (
            <TextInputSmall
              disabled={true}
              extraError={{ message: "invalidPolygon" }}
              lightTheme={true}
              label={t("form.size")}
              value={0}
            />
          ) : (
            <TextInputSmall
              disabled={true}
              extraError={getErrorForPath(issues, ["size"])}
              lightTheme={true}
              label={t("form.size")}
              value={size}
            />
          )}
        </div>
      </div>
      <div>
        <TextInputSmall
          disabled={!canEdit}
          extraError={getErrorForPath(issues, ["note"])}
          lightTheme={true}
          label={t("form.note")}
          value={data.note}
          onChange={handleTextChange("note")}
        />
      </div>
    </div>
  );
}

export default FacilityForm;
